import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowLeft, Edit2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Audit queue for the ≥3-verified-citations rule.
 * Surfaces every entry currently below the threshold so editors can work through
 * them WITHOUT taking the live site dark. Existing entries are grandfathered —
 * the rule only blocks NEW publishes.
 */
export default function AdminCitationAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit/citations");
      setData(r.data);
    } catch {
      toast.error("Failed to load audit queue");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-6 py-20 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading audit queue…</div>;
  }
  if (!data) return null;

  const grouped = {
    published: data.entries.filter((e) => e.review_state === "published"),
    pending_review: data.entries.filter((e) => e.review_state === "pending_review"),
    draft: data.entries.filter((e) => e.review_state === "draft"),
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6">
        <div className="overline">/editorial standards</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Citation audit</h1>
        <p className="text-base text-[#333] mt-3 max-w-2xl leading-relaxed">
          Receipts now requires <b>at least {data.min_verified_citations} verified, trustworthy sources</b> before
          a new entry can be published. Existing published entries below that bar
          are kept live to preserve the public record — they're listed here for
          editorial review. Add or verify additional citations to clear an entry from this queue.
        </p>
      </div>

      {/* Headline counts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/15 border border-black/15 mt-6">
        <Tile label="Below threshold" value={data.total_below_threshold} accent="alert" testid="audit-total" />
        <Tile label="Published & flagged" value={grouped.published.length} testid="audit-published" />
        <Tile label="Drafts / in review" value={grouped.draft.length + grouped.pending_review.length} testid="audit-draft" />
      </div>

      {data.total_below_threshold === 0 ? (
        <div className="mt-8 border-2 border-[#008A00] bg-[#F1F8F1] p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#008A00] text-white flex items-center justify-center">✓</div>
            <div>
              <div className="font-display text-2xl font-bold">All clear.</div>
              <p className="text-[#333] mt-1">Every entry meets the {data.min_verified_citations}-source standard.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <AuditSection title="Published — needs more sources"
                        subtitle="Live on the site. Add citations and verify them to clear this flag."
                        entries={grouped.published} threshold={data.min_verified_citations} />
          {grouped.pending_review.length > 0 && (
            <AuditSection title="Pending review" subtitle="" entries={grouped.pending_review} threshold={data.min_verified_citations} />
          )}
          {grouped.draft.length > 0 && (
            <AuditSection title="Drafts" subtitle="" entries={grouped.draft} threshold={data.min_verified_citations} />
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  const color = accent === "alert" ? "text-[#D32F2F]" : "text-black";
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className={`font-display text-4xl font-black mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function AuditSection({ title, subtitle, entries, threshold }) {
  if (entries.length === 0) return null;
  return (
    <div className="mt-10">
      <div className="overline mb-2">/{title.toLowerCase().replace(/\s+/g, "-")}</div>
      <h2 className="font-display text-2xl font-bold">{title}</h2>
      {subtitle && <p className="text-[#555] mt-1 text-sm">{subtitle}</p>}
      <div className="mt-4 border border-black/15 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono-plex uppercase text-[11px] tracking-[0.2em] text-[#555] bg-[#FAFAFA]">
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Jurisdiction</th>
              <th className="text-left px-4 py-3">Verified</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-black/10 hover:bg-[#FAFAFA]" data-testid={`audit-row-${e.id}`}>
                <td className="px-4 py-3 font-medium">
                  <Link to={`/entry/${e.id}`} className="hover:underline" target="_blank" rel="noreferrer">{e.title}</Link>
                </td>
                <td className="px-4 py-3 text-[#555]">{e.jurisdiction || "—"}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-[#D32F2F] font-mono-plex text-xs">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {e.verified_count} / {threshold}
                    <span className="text-[#888]">({e.citation_count} total)</span>
                  </span>
                </td>
                <td className="px-4 py-3 font-mono-plex text-xs text-[#555]">{e.date_action || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin/edit/${e.id}`}>
                    <Button variant="outline" size="sm" className="rounded-none border-black/40 h-8" data-testid={`audit-edit-${e.id}`}>
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
